/**
 * Accessibility Utility
 * Provides WCAG 3.0 AA compliance tools and accessibility enhancements
 */

export interface AccessibilityConfig {
  enableHighContrast: boolean;
  enableReducedMotion: boolean;
  enableScreenReader: boolean;
  enableKeyboardNavigation: boolean;
  enableFocusIndicators: boolean;
}

export class AccessibilityManager {
  private config: AccessibilityConfig;
  private focusableElements: HTMLElement[] = [];
  private currentFocusIndex = 0;

  constructor(config: Partial<AccessibilityConfig> = {}) {
    this.config = {
      enableHighContrast: true,
      enableReducedMotion: true,
      enableScreenReader: true,
      enableKeyboardNavigation: true,
      enableFocusIndicators: true,
      ...config,
    };

    this.initialize();
  }

  /**
   * Initialize accessibility features
   */
  private initialize(): void {
    this.setupMediaQueryListeners();
    this.setupKeyboardNavigation();
    this.setupFocusManagement();
    this.setupHighContrast();
    this.setupReducedMotion();
  }

  /**
   * Setup media query listeners for accessibility preferences
   */
  private setupMediaQueryListeners(): void {
    // High contrast preference
    const highContrastQuery = window.matchMedia('(prefers-contrast: high)');
    highContrastQuery.addEventListener('change', (e) => {
      this.config.enableHighContrast = e.matches;
      this.updateHighContrast();
    });

    // Reduced motion preference
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionQuery.addEventListener('change', (e) => {
      this.config.enableReducedMotion = e.matches;
      this.updateReducedMotion();
    });
  }

  /**
   * Setup keyboard navigation
   */
  private setupKeyboardNavigation(): void {
    if (!this.config.enableKeyboardNavigation) return;

    document.addEventListener('keydown', (e) => {
      switch (e.key) {
        case 'Tab':
          this.handleTabNavigation(e);
          break;
        case 'Escape':
          this.handleEscapeKey(e);
          break;
        case 'Enter':
        case ' ':
          this.handleActivationKeys(e);
          break;
        case 'ArrowUp':
        case 'ArrowDown':
        case 'ArrowLeft':
        case 'ArrowRight':
          this.handleArrowKeys(e);
          break;
      }
    });
  }

  /**
   * Handle Tab navigation
   */
  private handleTabNavigation(e: KeyboardEvent): void {
    this.updateFocusableElements();
    
    if (e.shiftKey) {
      // Shift + Tab: Navigate backwards
      if (this.currentFocusIndex > 0) {
        this.currentFocusIndex--;
      } else {
        this.currentFocusIndex = this.focusableElements.length - 1;
      }
    } else {
      // Tab: Navigate forwards
      if (this.currentFocusIndex < this.focusableElements.length - 1) {
        this.currentFocusIndex++;
      } else {
        this.currentFocusIndex = 0;
      }
    }

    this.focusableElements[this.currentFocusIndex]?.focus();
  }

  /**
   * Handle Escape key
   */
  private handleEscapeKey(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    
    // Close modals, dropdowns, etc.
    if (target.closest('[role="dialog"]')) {
      const closeButton = target.closest('[role="dialog"]')?.querySelector('[aria-label*="close"], [aria-label*="Close"]');
      if (closeButton instanceof HTMLElement) {
        closeButton.click();
      }
    }
  }

  /**
   * Handle activation keys (Enter, Space)
   */
  private handleActivationKeys(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    
    if (target.tagName === 'BUTTON' || target.getAttribute('role') === 'button') {
      e.preventDefault();
      target.click();
    }
  }

  /**
   * Handle arrow key navigation
   */
  private handleArrowKeys(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    
    // Handle radio groups
    if (target.getAttribute('role') === 'radio') {
      this.handleRadioGroupNavigation(e);
    }
    
    // Handle list navigation
    if (target.getAttribute('role') === 'listitem') {
      this.handleListNavigation(e);
    }
  }

  /**
   * Handle radio group navigation
   */
  private handleRadioGroupNavigation(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    const radioGroup = target.closest('[role="radiogroup"]');
    
    if (!radioGroup) return;
    
    const radios = Array.from(radioGroup.querySelectorAll('[role="radio"]')) as HTMLElement[];
    const currentIndex = radios.indexOf(target);
    
    let nextIndex = currentIndex;
    
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % radios.length;
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      nextIndex = currentIndex === 0 ? radios.length - 1 : currentIndex - 1;
    }
    
    radios[nextIndex].focus();
    (radios[nextIndex] as HTMLInputElement).checked = true;
  }

  /**
   * Handle list navigation
   */
  private handleListNavigation(e: KeyboardEvent): void {
    const target = e.target as HTMLElement;
    const list = target.closest('[role="list"]');
    
    if (!list) return;
    
    const listItems = Array.from(list.querySelectorAll('[role="listitem"]')) as HTMLElement[];
    const currentIndex = listItems.indexOf(target);
    
    let nextIndex = currentIndex;
    
    if (e.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % listItems.length;
    } else if (e.key === 'ArrowUp') {
      nextIndex = currentIndex === 0 ? listItems.length - 1 : currentIndex - 1;
    }
    
    listItems[nextIndex].focus();
  }

  /**
   * Setup focus management
   */
  private setupFocusManagement(): void {
    if (!this.config.enableFocusIndicators) return;

    // Add focus indicators to all focusable elements
    document.addEventListener('focusin', (e) => {
      const target = e.target as HTMLElement;
      target.classList.add('focus-visible');
    });

    document.addEventListener('focusout', (e) => {
      const target = e.target as HTMLElement;
      target.classList.remove('focus-visible');
    });

    // Handle focus trapping in modals
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        this.handleModalFocusTrap(e);
      }
    });
  }

  /**
   * Handle focus trapping in modals
   */
  private handleModalFocusTrap(e: KeyboardEvent): void {
    const modal = document.querySelector('[role="dialog"][aria-modal="true"]');
    if (!modal) return;

    const focusableElements = Array.from(
      modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ) as HTMLElement[];

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }

  /**
   * Update focusable elements list
   */
  private updateFocusableElements(): void {
    this.focusableElements = Array.from(
      document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter((el) => {
      const element = el as HTMLElement;
      const inputElement = el as HTMLInputElement | HTMLButtonElement | HTMLSelectElement | HTMLTextAreaElement;
      return !inputElement.disabled && element.offsetParent !== null;
    }) as HTMLElement[];
  }

  /**
   * Setup high contrast mode
   */
  private setupHighContrast(): void {
    if (this.config.enableHighContrast) {
      document.documentElement.classList.add('high-contrast');
    }
  }

  /**
   * Update high contrast mode
   */
  private updateHighContrast(): void {
    if (this.config.enableHighContrast) {
      document.documentElement.classList.add('high-contrast');
    } else {
      document.documentElement.classList.remove('high-contrast');
    }
  }

  /**
   * Setup reduced motion
   */
  private setupReducedMotion(): void {
    if (this.config.enableReducedMotion) {
      document.documentElement.classList.add('reduced-motion');
    }
  }

  /**
   * Update reduced motion mode
   */
  private updateReducedMotion(): void {
    if (this.config.enableReducedMotion) {
      document.documentElement.classList.add('reduced-motion');
    } else {
      document.documentElement.classList.remove('reduced-motion');
    }
  }

  /**
   * Add ARIA labels to elements
   */
  addAriaLabels(): void {
    // Add labels to buttons without text
    document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])').forEach((button) => {
      const buttonElement = button as HTMLButtonElement;
      if (!buttonElement.textContent?.trim()) {
        buttonElement.setAttribute('aria-label', 'Button');
      }
    });

    // Add labels to images without alt text
    document.querySelectorAll('img:not([alt])').forEach((img) => {
      img.setAttribute('alt', 'Image');
    });

    // Add labels to form inputs without labels
    document.querySelectorAll('input:not([aria-label]):not([aria-labelledby]):not([id])').forEach((input) => {
      const inputElement = input as HTMLInputElement;
      const placeholder = inputElement.placeholder;
      if (placeholder) {
        inputElement.setAttribute('aria-label', placeholder);
      }
    });
  }

  /**
   * Announce message to screen readers
   */
  announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    if (!this.config.enableScreenReader) return;

    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }

  /**
   * Skip to main content link
   */
  addSkipToMainContent(): void {
    const skipLink = document.createElement('a');
    skipLink.href = '#main-content';
    skipLink.textContent = 'Skip to main content';
    skipLink.className = 'skip-link sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg';
    
    document.body.insertBefore(skipLink, document.body.firstChild);
  }

  /**
   * Get accessibility report
   */
  getAccessibilityReport(): object {
    const issues: string[] = [];
    
    // Check for missing alt text
    const imagesWithoutAlt = document.querySelectorAll('img:not([alt])');
    if (imagesWithoutAlt.length > 0) {
      issues.push(`${imagesWithoutAlt.length} images missing alt text`);
    }

    // Check for missing labels
    const inputsWithoutLabels = document.querySelectorAll('input:not([aria-label]):not([aria-labelledby]):not([id])');
    if (inputsWithoutLabels.length > 0) {
      issues.push(`${inputsWithoutLabels.length} form inputs missing labels`);
    }

    // Check for missing ARIA labels
    const buttonsWithoutLabels = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby]):not([aria-describedby])');
    if (buttonsWithoutLabels.length > 0) {
      issues.push(`${buttonsWithoutLabels.length} buttons missing ARIA labels`);
    }

    return {
      timestamp: new Date().toISOString(),
      issues,
      totalIssues: issues.length,
      compliance: issues.length === 0 ? 'WCAG 3.0 AA Compliant' : 'Needs Improvement',
    };
  }
}

// Create singleton instance
export const accessibilityManager = new AccessibilityManager();

// Auto-initialize in development
if (process.env.NODE_ENV === 'development') {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      accessibilityManager.addAriaLabels();
      accessibilityManager.addSkipToMainContent();
    });
  } else {
    accessibilityManager.addAriaLabels();
    accessibilityManager.addSkipToMainContent();
  }
}

export default accessibilityManager;
